import { Box, Flex } from '@chakra-ui/react';
import React from 'react';

import { Button, H6, Icon } from 'DesignLibrary/atoms';
import { StackedIcon } from 'Shared/StackedIcon';
import Text from 'Shared/Text';
import { ProviderListProps } from 'Types/entities';
import { sendInsightsEvent } from 'Utils/insights';

const ProviderTag = ({
  name,
  testId,
  networkType,
  shouldShowPreferredProviderTiers,
}: {
  name: string;
  testId: string;
  networkType: string | undefined;
  shouldShowPreferredProviderTiers: boolean;
}) => (
  <Flex
    align="center"
    gap={1}
    bg={shouldShowPreferredProviderTiers ? 'var(--colors-blue-50)' : 'var(--colors-green-100)'}
    px={2}
    py={1}
    borderRadius="var(--border-radius-xs)"
    whiteSpace="nowrap"
  >
    {networkType === 'preferred-network' && shouldShowPreferredProviderTiers ? (
      <StackedIcon
        circleArgs={{ color: '--colors-alex-blue', size: 'small', weight: 'fill' }}
        dataTestId="preferred-network-docs-icon"
        stackedIcon={<Icon type="Star" size={11} color="--colors-blue-50" weight="fill" />}
      />
    ) : (
      <Icon color="--primary-green" size="small" weight="fill" type="CheckCircle" />
    )}

    <Box fontSize={12} fontWeight={700} data-testid={testId}>
      {name}
    </Box>
  </Flex>
);

const NoProviderTag = () => (
  <Flex bg="blackAlpha.50" px={2} py={1} borderRadius="var(--border-radius-xs)">
    <Box fontSize={12} fontWeight={700} color="var(--text-gray)" data-testid="in-network-provider-none">
      <Text field="health_section.recommendation.in_network_providers_none" />
    </Box>
  </Flex>
);

export const ProviderList = ({
  networkType,
  hasSavedProviders,
  onOpenProviderModal,
  providers,
  shouldShowPreferredProviderTiers = false,
  isMostPreferredEnabled = false,
  mostPreferredProviderName = '',
}: ProviderListProps) => {
  const headingText =
    networkType === 'preferred-network'
      ? 'health_section.recommendation.preferred_network_providers_heading'
      : 'health_section.recommendation.in_network_providers_heading';

  const renderInNetworkProviders =
    providers && providers?.length > 0 ? (
      <Flex gap={1} flexWrap={['wrap', 'nowrap']} overflowY="auto" justify={['flex-end', 'flex-start']}>
        {providers.map((provider, index) => (
          <ProviderTag
            name={`${provider?.first_name} ${provider?.middle_name ? `${provider.middle_name} ` : ''}${provider?.last_name}`}
            key={provider.npi}
            testId={`in-network-provider-${index}`}
            networkType={networkType}
            shouldShowPreferredProviderTiers={shouldShowPreferredProviderTiers}
          />
        ))}
      </Flex>
    ) : (
      <NoProviderTag />
    );

  return (
    <Flex align={['flex-start', 'center']} gap={3} overflow="hidden">
      <Box flexShrink={0}>
        <H6 color="--text-gray" data-testid="in-network-provider-heading">
          {isMostPreferredEnabled && mostPreferredProviderName ? (
            mostPreferredProviderName
          ) : (
            <Text field={headingText} />
          )}
        </H6>
      </Box>

      {hasSavedProviders ? (
        renderInNetworkProviders
      ) : (
        <Box sx={{ button: { borderStyle: 'dashed', borderWidth: '1px', fontSize: '14px' } }}>
          <Button
            iconLeft="Plus"
            iconProps={{
              size: 'small',
            }}
            size="xsmall"
            data-testid="provider-selection-button"
            onClick={() => {
              onOpenProviderModal();
              sendInsightsEvent(null, 'open_provider_search_modal');
            }}
          >
            <Text field="health_section.recommendation.in_network_providers_add_button" />
          </Button>
        </Box>
      )}
    </Flex>
  );
};
